import React, {useState} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {
  selectCitiesCache,
  selectHexes,
  selectPendingMovementActions,
  selectPendingShootingActions,
  selectPlayers,
  selectSelectedUnit,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {getUnitById} from "../../../utils/unit/getUnitById";
import {getPlayerById} from "../../../utils/players/getPlayerById";
import {rerollPendingShootingAction, showToast} from "../../../redux/actions";
import DamageDice from "../DamageDice/DamageDice";
import {getDamageDiceRoll} from "../../../utils/dice/getDamageDiceRoll";
import {getBotD6MinRoll} from "../../../utils/dice/getBotD6MinRoll";
import {getBotD6RollBonus} from "../../../utils/dice/getBotD6RollBonus";
import {getLowRatingAdvantageDicePenalty} from "../../../utils/dice/getLowRatingAdvantageDicePenalty";
import {getShotOpponentOwnerIds} from "../../../utils/shooting/getShotOpponentOwnerIds";
import {getPlayersByIds} from "../../../utils/players/getPlayersByIds";
import {
  TEXT_ALREADY_REROLLED,
  TEXT_CHOOSE_DICES_TO_REROLL,
  TEXT_UNIT_ALREADY_HAS_BEST_DAMAGE_RESULT,
  TEXT_UNIT_ALREADY_HAS_BUILDING_DAMAGE_RESULT,
  TEXT_UNIT_HAS_NO_ADDITIONAL_SUPPLY
} from "../../../constants/text/combat";
import {checkIfAlreadyBestResultForShootingD4} from "../../../utils/shooting/checkIfAlreadyBestResultForShootingD4";
import RerollButton, {SOUND_TIMEOUT_AFTER_REROLL} from "../RerollButton/RerollButton";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {playShotAnimation} from "../../../scenes/playShotAnimation";
import DamageBonus from "../DamageBonus/DamageBonus";
import {calculateShootingDamage} from "../../../utils/gameState/shooting/calculateShootingDamage";
import {ALL_BUILDING_CONFIGS} from "../../../configs/tokens";
import {D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE} from "../../../configs/war";
import {SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {checkIfKeyPoint} from "../../../utils/buildings/checkIfKeyPoint";
import {checkIfCurrentUserOwner} from "../../../utils/gameState/user/checkIfCurrentUserOwner";
import {selectUserId} from "../../../redux/reducers/privateStateReducer/selectors";
import {findOwner} from "../../../utils/gameState/findOwner";
import {checkIfUnitMeleeElf} from "../../../utils/factions/elves/checkIfUnitMeleeElf";
import {checkIfAlreadyBestMeleeCombatDamage} from "../../../utils/melee/checkIfAlreadyBestMeleeCombatDamage";
import {checkIfCentralQuarter} from "../../../utils/buildings/checkIfCentralQuarter";
import CentralQuarterIcon from "../CentralQuarterIcon/CentralQuarterIcon";
import {WAR_COLOR} from "../../../styles/constants";
import {
  CombatControls,
  CombatDiceGroup,
  CombatDuel,
  CombatEquation,
  CombatOperator,
  CombatPanel,
  CombatTerm,
  CombatToken,
  CombatTotal,
  CombatUnitToken,
  getCombatTargetSizeProps,
  getCombatUnitIconProps,
} from "../CombatPanel/CombatPanel";

// Какая из двух костей идет в выстрел: отряд стоял — лучшая, двигался — худшая
const DICE_CAPTION_BY_MODIFIER = {
  max: 'лучшая из двух',
  min: 'худшая из двух',
};

const getBuildingImageByToken = (tokenId) => {
  const building = ALL_BUILDING_CONFIGS.find(b => b.id === tokenId);
  return building ? building.image : null;
};

const SelectedUnitShooting = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const selectedUnit = useSelector(selectSelectedUnit);
  const players = useSelector(selectPlayers);
  const pendingShootingActions = useSelector(selectPendingShootingActions);
  const pendingMovementActions = useSelector(selectPendingMovementActions);
  const units = useSelector(selectUnits);
  const hexes = useSelector(selectHexes);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const userId = useSelector(selectUserId);
  const citiesCache = useSelector(selectCitiesCache)

  const [isDice1Selected, setIsDice1Selected] = useState(false);
  const [isDice2Selected, setIsDice2Selected] = useState(false);
  const [shouldBounceDices, setShouldBounceDices] = useState(false);

  if (!selectedUnit) {
    return null;
  }

  const isUnitMeleeElf = checkIfUnitMeleeElf(selectedUnit);

  const shootingAction = pendingShootingActions.find(action => action.shooterUnitId === selectedUnit.id);

  if (!shootingAction) {
    return null;
  }

  const isCurrentUserOwner = checkIfCurrentUserOwner(userId, selectedUnit.ownerId);

  const {
    id,
    hasRerolled,
    targetUnitId,
    targetHexKey,
    damage,
    diceRoll1,
    diceRoll2,
    bonus,
  } = shootingAction;

  const targetUnit = targetUnitId ? getUnitById(units, targetUnitId) : null;

  const targetToken = hexes[targetHexKey];
  let targetTokenImage;
  if (targetToken && targetToken !== TOKENS.ROAD && !checkIfKeyPoint(targetToken)) {
    targetTokenImage = getBuildingImageByToken(targetToken)
  }
  const targetSpecialBuilding = specialBuildings[targetHexKey];
  let targetSpecialBuildingImage;
  if (targetSpecialBuilding) {
    targetSpecialBuildingImage = getBuildingImageByToken(targetSpecialBuilding)
  }

  if (isUnitMeleeElf) {
    targetTokenImage = null;
    targetSpecialBuildingImage = null;
  }

  const hasMoved = pendingMovementActions.some(action => action.unitId === selectedUnit.id);
  const diceModifier = hasMoved ? 'min' : 'max'
  const isDice1Accounted = hasMoved ?
    diceRoll1 <= diceRoll2
    : diceRoll1 >= diceRoll2;
  const isDice2Accounted = !isDice1Accounted;

  // TODO: реализовать, но с проверкой уничтожения строений
  // const isTargetAlreadyKilled = targetUnitId
  //   ? checkIfUnitAlreadyReceivedMaxDamage(targetUnitId, units, pendingShootingActions)
  //   : false;
  const isAlreadyBestRollResult = isUnitMeleeElf ?
    checkIfAlreadyBestMeleeCombatDamage(diceRoll1, diceRoll2)
    : checkIfAlreadyBestResultForShootingD4(hasMoved, diceRoll1, diceRoll2);
  const isShootingOnlyBuildingAlreadySucceeded = !targetUnit && damage >= D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE;
  const isRerollDisabled = hasRerolled || !selectedUnit.additionalSupply || isAlreadyBestRollResult || isShootingOnlyBuildingAlreadySucceeded;

  const handleRerollClick = (onReroll) => {
    if (hasRerolled) {
      dispatch(showToast(TEXT_ALREADY_REROLLED, 'warning', 3000));
      return;
    }

    if (!selectedUnit.additionalSupply) {
      dispatch(showToast(TEXT_UNIT_HAS_NO_ADDITIONAL_SUPPLY, 'warning', 3000));
      return;
    }

    // if (isTargetAlreadyKilled) {
    //   dispatch(showToast(TEXT_TARGET_UNIT_ALREADY_KILLED, 'success', 3000))
    //   return;
    // }

    if (isAlreadyBestRollResult) {
      dispatch(showToast(TEXT_UNIT_ALREADY_HAS_BEST_DAMAGE_RESULT, 'success', 3000))
      return;
    }

    if (isShootingOnlyBuildingAlreadySucceeded) {
      dispatch(showToast(TEXT_UNIT_ALREADY_HAS_BUILDING_DAMAGE_RESULT, 'success', 3000))
      return;
    }

    if (!isDice1Selected && !isDice2Selected) {
      dispatch(showToast(TEXT_CHOOSE_DICES_TO_REROLL, 'info', 3000));
      setShouldBounceDices(true);
      setTimeout(() => setShouldBounceDices(false), 400);
      return;
    }

    // Кости принадлежат владельцу стреляющего отряда (см. calcMeleeCombatInput) —
    // у живых игроков подкруток нет. Кнопка достается и админу, управляющему
    // ботом (см. checkIfCurrentUserOwner): тогда при нем остаются пол и прибавка
    // уровня бонуса бота (пол против игрока под давлением здесь не учитывается),
    // если выстрел не по боту (см. checkIfAnyOpponentIsBot). Остается при нем и
    // штраф форы новичкам: по цели игрока с форой бот и перебрасывает со
    // штрафом к грани (см. getLowRatingAdvantageDicePenalty). Цели — те же, что
    // у первого броска в GameScene: по кому выстрел действительно бьет (см.
    // getShotOpponentOwnerIds)
    const targetUnitOwnerId = targetUnitId ? getUnitById(units, targetUnitId).ownerId : null;
    const targetHexOwnerId = findOwner(targetHexKey, players, citiesCache)?.id ?? null;

    const shooterOwner = getPlayerById(players, selectedUnit.ownerId);
    const shotTargetOwners = getPlayersByIds(
      players, getShotOpponentOwnerIds(selectedUnit, targetUnitOwnerId, targetHexOwnerId)
    );
    const rerollMinRoll = getBotD6MinRoll(shooterOwner, shotTargetOwners);
    const rerollBonus = getBotD6RollBonus(shooterOwner, shotTargetOwners);
    const rerollFacePenalty = getLowRatingAdvantageDicePenalty(shooterOwner, shotTargetOwners);
    const newDiceRoll1 = isDice1Selected
      ? getDamageDiceRoll(rerollMinRoll, rerollBonus, rerollFacePenalty)
      : diceRoll1;
    const newDiceRoll2 = isDice2Selected
      ? getDamageDiceRoll(rerollMinRoll, rerollBonus, rerollFacePenalty)
      : diceRoll2;

    const prevDamage = damage;
    const {
      damage: newDamage,
      bonus: newBonus
    } = calculateShootingDamage(selectedUnit, pendingMovementActions, newDiceRoll1, newDiceRoll2, units, players, targetUnitOwnerId, targetHexOwnerId)
    const isUnitTarget = !!targetUnit
    const isBadLuck = isUnitTarget || targetSpecialBuilding === SPECIAL_BUILDING_TOKENS.FORTRESS ?
      newDamage <= prevDamage : newDamage < D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE;

    dispatch(rerollPendingShootingAction(id, newDiceRoll1, newDiceRoll2, isUnitTarget, isBadLuck, isDice1Selected, isDice2Selected, newDamage, newBonus));
    // Переброс — выстрел заново: залп уходит после звука костей (как у
    // наблюдателей, см. playEffectForServerAction), а итог появляется на поле
    // с попаданием. Экшен могли отбросить (шаг обучения, блокировка ввода,
    // сюжетная реплика) — тогда залпа нет: isReroll сверяет это по состоянию
    playShotAnimation(store, REGISTRY_SINGLETON.getHexGridRenderer(), {
      actionId: id, delayMs: SOUND_TIMEOUT_AFTER_REROLL, isReroll: true,
    });

    onReroll(isDice1Selected, isDice2Selected, isBadLuck);

    setIsDice1Selected(false);
    setIsDice2Selected(false);
  }

  const shooterIconProps = getCombatUnitIconProps(selectedUnit, players);
  const targetHexOwnerColor = findOwner(targetHexKey, players, citiesCache)?.color;

  const targetTokens = [];
  if (targetUnit) {
    targetTokens.push({key: 'unit', isUnit: true});
  }
  if (targetTokenImage) {
    targetTokens.push({key: 'token', image: targetTokenImage, isCentralQuarter: checkIfCentralQuarter(targetToken)});
  }
  if (targetSpecialBuildingImage) {
    targetTokens.push({key: 'special', image: targetSpecialBuildingImage});
  }
  const targetSizeProps = getCombatTargetSizeProps(targetTokens.length);
  const targets = targetTokens.map(({key, isUnit, image, isCentralQuarter}) => (
    isUnit ? (
      <CombatUnitToken
        key={key}
        unitType={targetUnit.type}
        iconProps={getCombatUnitIconProps(targetUnit, players)}
        isTarget={true}
        {...targetSizeProps}
      />
    ) : (
      <CombatToken key={key} playerColor={targetHexOwnerColor} isTarget={true} {...targetSizeProps}>
        {isCentralQuarter
          ? <CentralQuarterIcon token={targetToken}/>
          : <img src={image} alt={'target building'}/>}
      </CombatToken>
    )
  ));

  const renderDice = (diceRoll, isDiceSelected, setDiceSelected, isDiceAccounted, diceIndex) => (
    <DamageDice
      key={`shooting-dice-${selectedUnit.id}-${diceIndex}`}
      ownerId={selectedUnit.ownerId}
      isShootingDice={true}
      isUnitMeleeElf={isUnitMeleeElf}
      hasAdditionalSupply={selectedUnit.additionalSupply}
      hasRerolled={hasRerolled}
      // isTargetAlreadyKilled={isTargetAlreadyKilled}
      isAlreadyBestRollResult={isAlreadyBestRollResult}
      damage={diceRoll}
      isAccounted={!isUnitMeleeElf && isDiceAccounted}
      isSelected={isDiceSelected}
      setSelected={setDiceSelected}
      shouldBounce={shouldBounceDices}
    />
  );

  return (
    <CombatPanel
      key={selectedUnit.id}
      toneColor={WAR_COLOR}
      duel={(
        <CombatDuel
          actor={<CombatUnitToken unitType={selectedUnit.type} iconProps={shooterIconProps}/>}
          actionIcon={<img src={'/img/icons/shooting.png'} alt={'Стрельба'} title={'Стрельба'}/>}
          targets={targets}
        />
      )}
    >
      <CombatEquation className={'shooting-toolbar_stats-container'}>
        {isUnitMeleeElf && <CombatOperator>{'('}</CombatOperator>}
        <CombatTerm value={selectedUnit.size} icon={'/img/icons/unit size.png'} iconAlt={'unit size'} title={'Размер отряда'}/>
        <CombatOperator>{'+'}</CombatOperator>
        <CombatDiceGroup caption={isUnitMeleeElf ? null : DICE_CAPTION_BY_MODIFIER[diceModifier]}>
          {renderDice(diceRoll1, isDice1Selected, setIsDice1Selected, isDice1Accounted, 1)}
          <CombatOperator>{isUnitMeleeElf ? '+' : ','}</CombatOperator>
          {renderDice(diceRoll2, isDice2Selected, setIsDice2Selected, isDice2Accounted, 2)}
        </CombatDiceGroup>
        {isUnitMeleeElf && <CombatOperator>{')'}</CombatOperator>}
        <DamageBonus bonus={bonus}/>
        {isUnitMeleeElf && <CombatOperator>{'/2'}</CombatOperator>}
      </CombatEquation>

      <CombatTotal value={damage} isDamage={true}/>

      {isCurrentUserOwner && (
        <CombatControls>
          <RerollButton
            handleRerollClick={handleRerollClick}
            isDisabledStyle={isRerollDisabled}
            isShooting={true}
            isShootingUnit={!!targetUnit}
            isUnitMeleeElf={isUnitMeleeElf}
          />
        </CombatControls>
      )}
    </CombatPanel>
  );
};

export default SelectedUnitShooting;
